import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles';
import { Card, Button, TextField, Tooltip, Fab } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { green } from '@material-ui/core/colors';
import _ from 'lodash';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { getDriverList, handleAddEditDriverDialog, createAgentDriver } from '../../../actions/driverActions';
import { useTranslation } from 'react-i18next';
import CommentIcon from '@material-ui/icons/Comment';
import DriverApplicationsModal from '../DriverApplicationsModal';
import { useToken, useAlert } from '../../../utilities/hooks';
import { getUserPreference } from '../../../utilities/common';
import UniDialog from "../../Common/UniDialog";
import UploadExcelDialog from "./UploadExcelDialog"
import FullscreenLoading from "../../Common/FullscreenLoading";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    paper: {
        padding: theme.spacing(2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    searchFab: {
        color: green[500],
        width: 40,
        height: 40
    },
    table: {
        minWidth: 700,
        border: 1,
    },
}))

const DRIVER_APPROVAL = 303;
const ADVANCED_EDIT = 304;
const CREATE_NEW = 320;

export default function ConditionalSearch(props) {
    const alertDialog = useAlert();
    const classes = useStyles();
    const { t } = useTranslation('editDriver');
    const dispatch = useDispatch();
    const tokenContents = useToken();
    const userUS = tokenContents.userUS;
    const [uploadExcelDialog, setUploadExcelDialog] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStarted, setUploadStarted] = useState(false);

    const userPref = getUserPreference();

    const userHasDriverApproval = tokenContents.uiAbilities.includes(DRIVER_APPROVAL);
    const userHasAdvancedEdit = tokenContents.uiAbilities.includes(ADVANCED_EDIT);
    const userHasCreateDriver = tokenContents.uiAbilities.includes(CREATE_NEW);

    const driverTypeList = [
        { label: t('all'), value: 9 },
        { label: t('service_number'), value: 1 },
        { label: t('full_time_driver'), value: -1 },
        { label: t('self_employee_driver'), value: 0 },
        { label: t('sorting_worker'), value: 10 },
    ];
    const driverStatusList = [
        { label: t('all'), value: 9 },
        { label: t('on_hire'), value: 1 },
        { label: t('off_hire'), value: 0 },
    ];

    const DEFAULT_DRIVER_TYPE = 9;
    const DEFAULT_DRIVER_STATUS = 9;
    const DEFAULT_START_PAGE = 0;
    const DEFAULT_ROWS_PER_PAGE = 20;

    const [searchDriverId, setSearchDriverId] = useState("");
    const [searchDriverPhone, setSearchDriverPhone] = useState("");

    const [driverType, setDriverType] = useState(DEFAULT_DRIVER_TYPE);       //default : -all drive type
    const [driverStatus, setDriverStatus] = useState(DEFAULT_DRIVER_STATUS); //default : -all driver status

    const [driverApplicationsOpen, setDriverApplicationsOpen] = useState(false);

    const onFileSubmit = async (file) => {
        try {
            setUploadStarted(true);
            const formData = new FormData();
            formData.append('operator', userPref.userId);
            formData.append('warehouse', userPref.userCity);
            formData.append('file', file);
            setUploadExcelDialog(false);
            const response = await dispatch(createAgentDriver(formData));
            const blob = response.data;
            if (blob instanceof Blob && blob.type === 'application/json') {
                const text = await blob.text();
                const jsonData = JSON.parse(text);
                const numbers = jsonData.data || [];
                const errorData = numbers.length > 0 ? numbers.join(", ") : "N/A";
                const errorMessageWithNumbers = `${t('dialog:phone_exist_message')} ${errorData}`;
                if (response.message) {
                    errorMessageWithNumbers = response.message + `${errorData}`;
                }
                alertDialog.addAlertDialog(t('dialog:download_failed'), errorMessageWithNumbers);
                alertDialog.showAlert();
            } else {
                alertDialog.addAlertDialog(t('dialog:download_success'));
            }
        } catch (error) {
            alertDialog.addAlertDialog(t('dialog:download_failed'), error.message || error.additional);
            alertDialog.showAlert();
            console.error(error);
        } finally {
            setUploadStarted(false);
        }
    };

    return (
        <Card >
            <form style={{ display: 'flex', flexDirection: 'row', padding: 16 }} onSubmit={e => { e.preventDefault(); }}>
                <div style={{ display: 'flex', width: '100%' }}>
                    <TextField
                        id="edit_driver_search_driver_type_textfield"
                        select
                        SelectProps={{ native: true }}
                        label={t('list_driver_type')}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        value={driverType}
                        onChange={(e) => { setDriverType(parseInt(e.target.value)); }}
                        style={{ width: 200, paddingRight: 16 }}
                    >
                        {driverTypeList.map((item, index) => (
                            <option key={item + index} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        id="edit_driver_search_driver_status_textfield"
                        select
                        SelectProps={{ native: true }}
                        label={t('List_driver_status')}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        value={driverStatus}
                        onChange={(e) => { setDriverStatus(parseInt(e.target.value)); }}
                        style={{ width: 200, paddingRight: 16 }}
                    >
                        {driverStatusList.map((item, index) => (
                            <option key={item + index} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        id="edit_driver_search_driver_id_textfield"
                        label={t('searchDriverId')}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        value={searchDriverId}
                        onChange={(e) => { setSearchDriverId(e.target.value); }}
                        style={{ width: 200, paddingRight: 16 }}
                    />
                    <TextField
                        id="edit_driver_search_driver_phone_textfield"
                        label={t('searchDriverPhone')}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        value={searchDriverPhone}
                        onChange={(e) => { setSearchDriverPhone(e.target.value); }}
                        style={{ width: 200, paddingRight: 16 }}
                    />
                    <Button
                        color='primary'
                        onClick={handleSearchPrePare}
                        variant='contained'
                        id='edit_driver_search_search_button'
                    >
                        {t('search')}
                    </Button>
                    <div style={{ display: 'flex', marginLeft: 60, alignItems: 'center' }}>
                        <Tooltip title={t('tip_reset')} aria-label="reset" >
                            <Fab className={classes.searchFab}>
                                <RefreshIcon
                                    onClick={handleRest}
                                    id='edit_driver_search_reset_button'
                                ></RefreshIcon>
                            </Fab>
                        </Tooltip>
                        <div style={{ marginLeft: 30, width: 30, borderLeft: '1px #636463 solid', height: 40 }}>
                        </div>
                        {
                            userHasCreateDriver &&
                            <Tooltip title={t('tip_add')} aria-label="add" >
                                <Fab className={classes.searchFab}>
                                    <AddCircleIcon
                                        onClick={handleOpenAddDriver}
                                        id='edit_driver_search_add_button'
                                    ></AddCircleIcon>
                                </Fab>
                            </Tooltip>
                        }
                        {
                            userHasDriverApproval &&
                            <Tooltip title={t('tip_driver_approval')} aria-label="approval" >
                                <Fab
                                    className={classes.searchFab}
                                    style={{
                                        color: '#dd4b3f',
                                        marginLeft: 60
                                    }}
                                >
                                    <CommentIcon
                                        onClick={() => setDriverApplicationsOpen(true)}
                                        id='edit_driver_search_application_button'
                                    ></CommentIcon>
                                </Fab>
                            </Tooltip>
                        }
                        {
                            userUS && userHasCreateDriver &&
                            <Tooltip title={t('tip_driver_approval')} aria-label="approval" >
                                <Fab
                                    className={classes.searchFab}
                                    style={{
                                        color: '#dd4b3f',
                                        marginLeft: 60
                                    }}
                                >
                                    <CloudUploadIcon
                                        onClick={() => setUploadExcelDialog(true)}
                                        id='edit_driver_search_application_button'
                                    ></CloudUploadIcon>
                                </Fab>
                            </Tooltip>
                        }
                    </div>
                </div>
            </form>
            <UniDialog
                title={t('common:upload')}
                open={uploadExcelDialog}
                onClose={() => setUploadExcelDialog(false)}
                onFinish={() => onFileSubmit(excelFile)}
                autoClose={false}
                id="edit_driver_uploadexcel_unidialog"
                maxWidth="xs"
                fullWidth
            >
                <UploadExcelDialog
                    id="edit_driver_uploadexcel_dialog"
                    onDataChange={(file) => setExcelFile(file)}
                    progress={uploadStarted}
                />
            </UniDialog>
            <DriverApplicationsModal
                open={driverApplicationsOpen}
                id="edit_driverapplication_modal"
                handleClose={handleDriverApplicationsClose}
            />
            <FullscreenLoading open={uploadStarted} />
        </Card>

    );

    function handleDriverApplicationsClose() {
        setDriverApplicationsOpen(false);
    }

    function init() {
        setDriverType(DEFAULT_DRIVER_TYPE);
        setDriverStatus(DEFAULT_DRIVER_STATUS);
        setSearchDriverId("");
        setSearchDriverPhone("");
    }

    function handleSearchPrePare() {
        props.handleSearch(
            userPref.userCity,
            driverType,
            driverStatus,
            searchDriverId,
            searchDriverPhone,
            DEFAULT_START_PAGE,
            DEFAULT_ROWS_PER_PAGE,
            "conditional_search"
        );
    }

    function handleOpenAddDriver() {
        dispatch(handleAddEditDriverDialog(true, 'add'));
    }

    function handleRest() {
        init();
    }
}
